import { PropsWithChildren, useEffect, useState } from "react";
import styles from "./App.module.scss";

import { TemplatesPage } from "pages/OwnOffice/TemplatesPage";
import {
  EquipmentsPage,
  TrainPage,
  TrainsPage,
  MorePage,
  OwnOfficeMain,
  CustomersPage,
  ProgressPage,
  RecomendTemplatePage,
  RecomendTrainPage,
  DashboardPage,
  SubscriptionPage,
  PurchasePage,
  WorkoutPreviewPage,
  WorkoutExercisePage,
  EditTrainingPage,
  TestimonialsPage,
  AIChatPage,
} from "./pages/";

import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { ClientPage } from "pages/OwnOffice/ClientPage";
import { AdminPage } from "./pages/AdminPage";
import { AdminNotificationsPage } from "./pages/AdminNotificationsPage";
import {
  AdminChatAncetPage,
  AdminChatEquipmentsPage,
  AdminChatLogsPage,
  AdminChatMessagesPage,
  AdminChatSummaryPage,
  AdminChatTrainingsPage,
  AdminChatTrainingsProgramPage,
  AdminChatEditTrainingPage,
  AdminChatUserLayout,
  AdminChatsLayout,
  AdminChatsListPage,
} from "./pages/AdminChatsPage";
import {
  TrainerChatsLayout,
  TrainerChatsListPage,
  TrainerChatUserLayout,
  TrainerChatSummaryPage,
  TrainerChatAncetPage,
  TrainerChatTrainingsPage,
  TrainerChatTrainingsProgramPage,
  TrainerChatEditTrainingPage,
  TrainerChatEquipmentsPage,
  TrainerChatMessagesPage,
} from "./pages/TrainerChatsPage";
import { UserPage } from "./pages/UserPage";
import { UsersPage } from "./pages/UsersPage";
import { OnboardingProvider, UsersProvider } from "./shared/context";
import { SurveyModal } from "./widgets";
import { User } from "./entities/user";
import { useGetUserMutation } from "./store/usersApi";
import { getTelegramWebApp } from "./constants/telegram";
import { Menu, Preloader, TestimonialsWrapper } from "./components";
import { TopDecorations } from "./components/TopDecorations";
import { OnboardingPage } from "./pages/onboarding";
import { WayToGoal } from "./pages/way-to-goal";
import { BodyForm } from "./pages/body-form";
import { GirlBodyForm } from "./pages/girl-body-form";
import { QuizPage } from "./pages/quiz";
import { UserStatus } from "./pages/TrainsPage/TrainsPage";
import { usePageTracking } from "./hooks/usePageTracking";
import { initTelegramWebApp } from "./shared/utils/telegramInit";
import { LoginPage } from "./features/auth/ui/LoginPage";
import { RegisterPage } from "./features/auth/ui/RegisterPage";
import { useAppSelector, useAppDispatch } from "./shared/hooks";
import { loadAuthFromStorage } from "./features/auth/model/authSlice";
import { ProtectedRoute } from "./shared/components/ProtectedRoute";

const AppWithTracking = () => {
  usePageTracking();
  return null;
};

type AppPageWrapperProps = PropsWithChildren<{
  showMobileTopDecorations: boolean;
}>;

const AppPageWrapper = ({ children, showMobileTopDecorations }: AppPageWrapperProps) => {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith("/admin");

  return (
    <div
      className={
        isAdminPath
          ? ""
          : `${styles.appPageWrapper} ${
              showMobileTopDecorations ? "" : styles.appPageWrapperNoTopInset
            }`
      }
    >
      {!isAdminPath && showMobileTopDecorations && <TopDecorations />}
      {children}
    </div>
  );
};

function App() {
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState<boolean>(false);
  const [isShowModal, setShowModal] = useState<boolean>(false);
  const [initData, setInitData] = useState<string>();
  const [createUser, { isLoading: isUserLoading }] = useGetUserMutation();
  const [user, setUser] = useState<User | null>(null);
  const [userLoadError, setUserLoadError] = useState<boolean>(false);
  const [userLoadAttempted, setUserLoadAttempted] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { isAuthenticated, userId } = useAppSelector((state) => state.auth);
  const hasTokenAuth =
    typeof window !== "undefined" && !!localStorage.getItem("access_token");
  const hasWebAuth = isAuthenticated || hasTokenAuth;
  const hasAnyAuth = !!initData || hasWebAuth;

  const handleSurveyModalClose = () => {
    setIsSurveyModalOpen(false);
  };

  useEffect(() => {
    // Загружаем JWT авторизацию из localStorage
    dispatch(loadAuthFromStorage());
    
    const initializeTelegram = () => {
      try {
        // Инициализируем Telegram Web App с правильными настройками
        initTelegramWebApp();

        // Вызываем requestFullscreen при инициализации приложения только на мобильных устройствах
        const webApp = getTelegramWebApp();
        if (webApp) {
          const isMobile = webApp.platform === "ios" || webApp.platform === "android";
          const isAdminPath = window.location.pathname.startsWith("/admin");
          if (isMobile && !isAdminPath && webApp.requestFullscreen) {
            webApp.requestFullscreen();
          }

          // Получаем initData, если оно доступно
          const currentInitData = webApp.initData;
          if (currentInitData) {
            setInitData((prev) => prev || currentInitData);
            return;
          }
        }
      } catch (error) {
        console.error("Error in App initialization:", error);
      }
    };

    // Пытаемся инициализировать сразу
    initializeTelegram();

    // Если initData не получен сразу, пытаемся еще раз через небольшую задержку
    // (в Telegram Web App initData может быть доступен не сразу)
    const timeoutId = setTimeout(() => {
      const webApp = getTelegramWebApp();
      if (webApp && webApp.initData && !initData) {
        setInitData(webApp.initData);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [dispatch]);

  // Отдельный useEffect для авторизации - срабатывает когда initData становится доступным или JWT авторизация
  useEffect(() => {
    const handleCreateUser = async () => {
      const currentPath = window.location.pathname;
      if (currentPath === "/login" || currentPath === "/register") {
        setUserLoadAttempted(false);
        setUserLoadError(false);
        return;
      }

      const hasTelegramAuth = !!initData;
      const hasJwtAuth = isAuthenticated && !!userId;
      const hasTokenAuth = !!localStorage.getItem("access_token");

      // Если нет авторизации, сбрасываем user
      if (!hasTelegramAuth && !hasJwtAuth && !hasTokenAuth) {
        if (user) {
          setUser(null);
        }
        setUserLoadAttempted(false);
        setUserLoadError(false);
        return;
      }

      // Если user уже установлен и авторизация не изменилась, не делаем повторный запрос
      // Но если авторизация изменилась (например, после логина), нужно обновить user
      if (user && hasJwtAuth && isAuthenticated) {
        // Проверяем, не изменился ли userId
        const storedUserId = localStorage.getItem("user_id");
        if (storedUserId && parseInt(storedUserId, 10) === userId) {
          // User уже загружен для этого userId, не делаем повторный запрос
          setUserLoadAttempted(true);
          setUserLoadError(false);
          return;
        }
      }

      // Устанавливаем флаг попытки загрузки
      setUserLoadAttempted(true);
      setUserLoadError(false);

      try {
        // Добавляем таймаут для загрузки (10 секунд)
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Timeout")), 10000);
        });

        const result = await Promise.race([
          createUser().unwrap(),
          timeoutPromise,
        ]) as User;

        setUser(result || null);
        setUserLoadError(false);

        if (result?.select_samples === UserStatus.REQUIRED_ANCET) {
          setShowModal(true);
          setIsSurveyModalOpen(true);
        }
      } catch (err: any) {
        console.error("Error loading user:", err);
        setUserLoadError(true);
        
        // Если ошибка 401, очищаем авторизацию и редиректим на логин
        if (err?.status === 401 || err?.status === 'FETCH_ERROR' || err?.data?.statusCode === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("user_id");
          setUser(null);
          setInitData(undefined);
          // Редирект произойдет автоматически через роутинг
        }
      }
    };

    handleCreateUser();
  }, [initData, isAuthenticated, userId, createUser]);

  // Показываем прелоадер только если идет загрузка И еще не было попытки загрузки ИЛИ не было ошибки
  const shouldShowPreloader = isUserLoading && hasAnyAuth && !userLoadError && (!userLoadAttempted || !user);
  
  if (shouldShowPreloader) {
    return (
      <div>
        <Router>
          <AppWithTracking />
          <UsersProvider>
            <Menu />
          </UsersProvider>
        </Router>

        <Preloader hasMenu />
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-center" />
      <SurveyModal
        isOpen={isSurveyModalOpen && isShowModal}
        onClose={handleSurveyModalClose}
      />

      <Router>
        <AppWithTracking />
        <UsersProvider>
          <OnboardingProvider user={user}>
            <TestimonialsWrapper>
              <AppPageWrapper showMobileTopDecorations={Boolean(initData)}>
              <Routes>
              <Route
                path="/login"
                element={hasWebAuth ? <Navigate to="/trains" replace /> : <LoginPage />}
              />
              <Route
                path="/register"
                element={hasWebAuth ? <Navigate to="/trains" replace /> : <RegisterPage />}
              />
              
              {/* Корневой путь - редирект в зависимости от авторизации */}
              <Route 
                path="/" 
                element={
                  hasAnyAuth ? (
                    <Navigate to="/trains" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              
              {/* Защищенные маршруты */}
              <Route
                path="/more"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    <MorePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/trains"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    {user ? (
                      <TrainsPage initData={initData || ""} user={user} />
                    ) : (isUserLoading && !userLoadError) ? (
                      <Preloader hasMenu />
                    ) : userLoadError ? (
                      <Navigate to="/login" replace />
                    ) : (
                      <Navigate to="/login" replace />
                    )}
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/train-page/:id"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    {user ? (
                      <TrainPage initData={initData || ""} user={user} />
                    ) : (
                      <Navigate to="/login" replace />
                    )}
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/edit-training/:id"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    {user ? (
                      <EditTrainingPage initData={initData || ""} user={user} />
                    ) : (
                      <Navigate to="/login" replace />
                    )}
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/equipments"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    {user ? (
                      <EquipmentsPage user={user} />
                    ) : (
                      <Navigate to="/login" replace />
                    )}
                  </ProtectedRoute>
                }
              />

              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    <OnboardingPage initData={initData || ""} />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/way-to-goal"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    <WayToGoal />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/body-form"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    <BodyForm initData={initData || ""} />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/girl-body-form"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    <GirlBodyForm initData={initData || ""} />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    <SubscriptionPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/subscription/:id"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    <PurchasePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/ai-chat"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    <AIChatPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/quiz"
                element={
                  <ProtectedRoute initData={initData} isAuthenticated={isAuthenticated}>
                    {isUserLoading ? (
                      <Preloader hasMenu />
                    ) : (
                      <QuizPage initData={initData} />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recomend-template/:id"
                element={<RecomendTemplatePage />}
              />
              <Route
                path="/recomend-train-page/:id"
                element={<RecomendTrainPage />}
              />
              <Route
                path="/workout-preview/:id"
                element={<WorkoutPreviewPage />}
              />
              <Route
                path="/workout-exercise/:trainingId/:exerciseId"
                element={<WorkoutExercisePage />}
              />
              <Route path="/own-office" element={<OwnOfficeMain />} />
              <Route path="/own-office/customers" element={<CustomersPage />} />
              <Route path="/own-office/stats" element={<TemplatesPage />} />
              <Route path="/own-office/customers/1" element={<ClientPage />} />
              <Route path="/progress-page" element={<ProgressPage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />

              <Route path="/admin/" element={<AdminPage />} />
              <Route path="/admin/chats" element={<AdminChatsLayout />}>
                <Route index element={<AdminChatsListPage />} />
                <Route path=":userId" element={<AdminChatUserLayout />}>
                  <Route index element={<AdminChatSummaryPage />} />
                  <Route path="ancet" element={<AdminChatAncetPage />} />
                  <Route path="logs" element={<AdminChatLogsPage />} />
                  <Route path="trainings" element={<AdminChatTrainingsPage />} />
                  <Route path="trainings-program" element={<AdminChatTrainingsProgramPage />} />
                  <Route path="edit-training/:id" element={<AdminChatEditTrainingPage />} />
                  <Route path="equipments" element={<AdminChatEquipmentsPage />} />
                  <Route path="chat" element={<AdminChatMessagesPage />} />
                </Route>
              </Route>
              <Route path="/trainer/clients" element={<TrainerChatsLayout />}>
                <Route index element={<TrainerChatsListPage />} />
                <Route path=":userId" element={<TrainerChatUserLayout />}>
                  <Route index element={<TrainerChatSummaryPage />} />
                  <Route path="ancet" element={<TrainerChatAncetPage />} />
                  <Route path="trainings" element={<TrainerChatTrainingsPage />} />
                  <Route path="trainings-program" element={<TrainerChatTrainingsProgramPage />} />
                  <Route path="edit-training/:id" element={<TrainerChatEditTrainingPage />} />
                  <Route path="equipments" element={<TrainerChatEquipmentsPage />} />
                  <Route path="chat" element={<TrainerChatMessagesPage />} />
                </Route>
              </Route>
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/users/user/:id" element={<UserPage />} />
              <Route path="/admin/dashboard" element={<DashboardPage />} />
              <Route path="/admin/notifications" element={<AdminNotificationsPage />} />
              
              {/* Редирект для всех остальных неавторизованных путей */}
              <Route 
                path="*" 
                element={
                  hasAnyAuth ? (
                    <Navigate to="/trains" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              </Routes>
              </AppPageWrapper>
            </TestimonialsWrapper>
          </OnboardingProvider>
        </UsersProvider>
      </Router>
    </div>
  );
}

export default App;

